import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';

import { DataCard } from '@models/module/main';
import { DataListViewAction } from '@models/module/main/dataListViewAction';
import {
  DATA_CARD_FRAME_CSS,
  DATA_CARD_GRID_CSS,
  getDataCardPartsGridColumns,
  getDataCardPartsGridHeight,
} from '@module-instance/data-card/data-card-layout';
import { DataCardViewPartComponent } from '@module-instance/data-card/data-card-view-part/data-card-view-part.component';
import { TranslateModule } from '@ngx-translate/core';
import { DropdownActionsEnum } from '@shared/consts';
import { removeRedundantMenuDividers } from '@shared/utils/menu.utils';

@Component({
  selector: 'app-data-card-view',
  templateUrl: './data-card-view.component.html',
  styleUrls: ['./data-card-view.component.scss'],
  imports: [MatMenuModule, DataCardViewPartComponent, MatButtonModule, MatIconModule, TranslateModule],
  // The host pulls itself out by this much on every side; the same constant is subtracted when the
  // virtual row height is calculated, so the two can never disagree. See data-card-layout.ts.
  host: {
    '[style.--data-card-frame-offset]': 'frameCss.hostOffset',
    '[style.--data-card-frame-negative-offset]': 'frameCss.negativeHostOffset',
    '[style.--data-card-grid-row-height]': 'gridCss.rowHeight',
    '[style.--data-card-grid-gap]': 'gridCss.gap',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DataCardViewComponent {
  readonly frameCss = DATA_CARD_FRAME_CSS;
  readonly gridCss = DATA_CARD_GRID_CSS;

  card = input.required<DataCard>();
  actions = input<DataListViewAction[]>([]);

  readonly dividerActionType = String(DropdownActionsEnum.DIVIDER);
  actionsFiltered = computed(() =>
    removeRedundantMenuDividers(
      this.actions().filter(action =>
        action.actionType === this.dividerActionType
          ? !action.condition || action.condition(this.card())
          : action.alwaysOnRow === true && action.condition?.(this.card()),
      ),
      action => action.actionType === this.dividerActionType,
    ),
  );

  readonly gridLayout = computed(() => {
    const parts = this.card().parts;
    return {
      columns: getDataCardPartsGridColumns(parts),
      height: getDataCardPartsGridHeight(parts.map(part => ({ y: part.y, rows: part.effectiveRows }))),
    };
  });

  cardActionClick(action: DataListViewAction) {
    if (action.click) {
      action.click(this.card());
    }
  }
}
